import { zodResolver } from '@hookform/resolvers/zod'
import { InputWrapperGridStyled } from '@theme'
import { ReactNode, useEffect, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Button, Input, TextArea } from 'src/common'
import { CustomDragger } from 'src/common/upload/CustomDragger'
import { AppModal } from 'src/components/AppModal'
import { XCloseIcon } from 'src/components/Icon'
import SelectMedia from 'src/components/gallery/SelectMedia'
import SelectMediaModal from 'src/components/gallery/SelectMediaModal'
import { EMediaCategory, EMediaType, IMediaItem } from 'src/interfaces/gallery'
import { EUploadFileType } from '@configs'
import { IMegaMenuItem } from 'src/interfaces/mega-menu'
import { Switch } from 'antd'
import { z } from 'zod'
import { t } from 'i18next'

// Define the interface for mega menu request
export interface IAddMegaMenuRequest {
  title?: string
  titleInDutch?: string
  subTitle?: string
  subTitleInDutch?: string
  description?: string
  descriptionInDutch?: string
  isComing?: boolean
  imageUrl?: string
  logoUrl?: string
  imageMediaId?: number
  logoMediaId?: number
}

type MegaMenuDetailModalProps = {
  open: boolean
  isLoading: boolean
  onClose: () => void
  title?: string
  content?: ReactNode
  onSave: (data: IAddMegaMenuRequest) => void
  item?: IMegaMenuItem | null
  changeLoading?: (value: boolean) => void
}

const addMegaMenuSchema = z.object({
  title: z
    .string()
    .trim()
    .nonempty({
      message: t('error:field_required') as string,
    })
    .max(50, {
      message: 'Title must be 50 characters or less',
    }),
  titleInDutch: z
    .string()
    .trim()
    .nonempty({
      message: t('error:field_required') as string,
    })
    .max(50, {
      message: 'Title in Dutch must be 50 characters or less',
    }),
  subTitle: z
    .string()
    .trim()
    .max(100, {
      message: 'Sub Title must be 100 characters or less',
    })
    .optional(),
  subTitleInDutch: z
    .string()
    .trim()
    .max(100, {
      message: 'Sub Title in Dutch must be 100 characters or less',
    })
    .optional(),
  description: z
    .string()
    .trim()
    .max(255, {
      message: 'Description must be 255 characters or less',
    })
    .optional(),
  descriptionInDutch: z
    .string()
    .trim()
    .max(255, {
      message: 'Description in Dutch must be 255 characters or less',
    })
    .optional(),
  isComing: z.boolean().optional(),
  imageUrl: z.string().optional(),
  logoUrl: z.string().optional(),
})

export const MegaMenuDetailModal = (props: MegaMenuDetailModalProps) => {
  const { open, onClose, onSave, isLoading, item, changeLoading } = props

  const [imageSrc, setImageSrc] = useState(item?.imageUrl ?? '')
  const [logoSrc, setLogoSrc] = useState(item?.logoUrl ?? '')
  const [showModalSelectMedia, setShowModalSelectMedia] = useState(false)
  const [selectedField, setSelectedField] = useState<'image' | 'logo' | null>(
    null
  )
  const onChangeImageRef = useRef<any>(null)
  const onChangeLogoRef = useRef<any>(null)

  const defaultValue: IAddMegaMenuRequest = {
    title: item?.title ?? '',
    titleInDutch: item?.titleInDutch ?? '',
    subTitle: item?.subTitle ?? '',
    subTitleInDutch: item?.subTitleInDutch ?? '',
    description: item?.description ?? '',
    descriptionInDutch: item?.descriptionInDutch ?? '',
    isComing: item?.isComing ?? false,
    imageUrl: item?.imageUrl ?? '',
    logoUrl: item?.logoUrl ?? '',
    imageMediaId: 0,
    logoMediaId: 0,
  }

  const { control, handleSubmit, setValue } = useForm<IAddMegaMenuRequest>({
    defaultValues: defaultValue,
    resolver: zodResolver(addMegaMenuSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  })

  const onCloseModalSelectMedia = () => {
    setShowModalSelectMedia(false)
  }

  const onSelectMedia = (data: IMediaItem) => {
    if (selectedField === 'image') {
      setImageSrc(data?.baseUrl ?? data?.url ?? data?.signedUrl ?? '')
      setValue('imageMediaId', data?.id ?? 0)
      setValue('imageUrl', data?.baseUrl ?? data?.url ?? data?.signedUrl ?? '')
      onChangeImageRef.current(data?.id ?? 0)
    } else if (selectedField === 'logo') {
      setLogoSrc(data?.baseUrl ?? data?.url ?? data?.signedUrl ?? '')
      setValue('logoMediaId', data?.id ?? 0)
      setValue('logoUrl', data?.baseUrl ?? data?.url ?? data?.signedUrl ?? '')
      onChangeLogoRef.current(data?.id ?? 0)
    }

    setShowModalSelectMedia(false)
  }

  useEffect(() => {
    setValue('title', item?.title ?? '')
    setValue('titleInDutch', item?.titleInDutch ?? '')
    setValue('subTitle', item?.subTitle ?? '')
    setValue('subTitleInDutch', item?.subTitleInDutch ?? '')
    setValue('description', item?.description ?? '')
    setValue('descriptionInDutch', item?.descriptionInDutch ?? '')
    setValue('isComing', item?.isComing ?? false)
    setValue('imageUrl', item?.imageUrl ?? '')
    setValue('logoUrl', item?.logoUrl ?? '')
    setImageSrc(item?.imageUrl ?? '')
    setLogoSrc(item?.logoUrl ?? '')
  }, [open, item, setValue])

  return (
    <AppModal open={open} onClose={onClose}>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="m-0 text-[20px]">
            {item ? 'Mega Menu Detail' : 'Add Mega Menu'}
          </h1>
        </div>
        <div className="hover:opacity-75 cursor-pointer">
          <XCloseIcon width={16} height={16} onClick={onClose} />
        </div>
      </div>

      {/* Coming Soon switch under the header */}
      <div className="mt-3">
        <Controller
          name="isComing"
          control={control}
          render={({ field: { value, onChange } }) => (
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Coming Soon:</label>
              <Switch checked={value} onChange={onChange} size="small" />
            </div>
          )}
        />
      </div>
      <div className="mt-6">
        <form>
          <InputWrapperGridStyled>
            <Controller
              name={'title'}
              control={control}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => {
                return (
                  <Input
                    label="Title"
                    name="title"
                    required
                    onChange={onChange}
                    value={value}
                    alignment="col"
                    errors={error?.message}
                    placeholder="Enter title"
                  />
                )
              }}
            />

            <Controller
              name={'titleInDutch'}
              control={control}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => {
                return (
                  <Input
                    label="Title in Dutch"
                    name="titleInDutch"
                    required
                    onChange={onChange}
                    value={value}
                    alignment="col"
                    errors={error?.message}
                    placeholder="Enter title in Dutch"
                  />
                )
              }}
            />

            <Controller
              name={'subTitle'}
              control={control}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => {
                return (
                  <Input
                    label="Subtitle"
                    name="subTitle"
                    onChange={onChange}
                    value={value}
                    alignment="col"
                    errors={error?.message}
                    placeholder="Enter subtitle"
                    disabled={item?.parent?.key === 'home'}
                  />
                )
              }}
            />

            <Controller
              name={'subTitleInDutch'}
              control={control}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => {
                return (
                  <Input
                    label="Subtitle in Dutch"
                    name="subTitleInDutch"
                    onChange={onChange}
                    value={value}
                    alignment="col"
                    errors={error?.message}
                    placeholder="Enter subtitle in Dutch"
                    disabled={item?.parent?.key === 'home'}
                  />
                )
              }}
            />

            <Controller
              name="description"
              control={control}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => {
                return (
                  <TextArea
                    label="Description"
                    name="description"
                    onChange={onChange}
                    value={value}
                    alignment="col"
                    errors={error?.message}
                    containerClassName="mt-3"
                    placeholder="Enter description"
                  />
                )
              }}
            />

            <Controller
              name="descriptionInDutch"
              control={control}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => {
                return (
                  <TextArea
                    label="Description in Dutch"
                    name="descriptionInDutch"
                    onChange={onChange}
                    value={value}
                    alignment="col"
                    errors={error?.message}
                    containerClassName="mt-3"
                    placeholder="Enter description in Dutch"
                  />
                )
              }}
            />

            <div className="flex flex-col gap-1">
              <Controller
                name="imageMediaId"
                control={control}
                render={({ field: { onChange }, fieldState: { error } }) => {
                  onChangeImageRef.current = onChange
                  return (
                    <CustomDragger
                      label="Image"
                      name="imageMediaId"
                      id="imageMediaId"
                      containerClassName="mt-3"
                      alignment="col"
                      onLoadEnd={(data) => {
                        setValue('imageMediaId', data?.id ?? 0)
                        setValue(
                          'imageUrl',
                          data?.url || data?.original || data?.baseUrl || ''
                        )
                        onChange(data?.id ?? 0)
                        setImageSrc(
                          data?.url || data?.original || data?.baseUrl || ''
                        )
                      }}
                      errors={error?.message}
                      initResource={imageSrc}
                      reset={!imageSrc}
                      allowFileTypes={['image/png', 'image/jpeg', 'image/jpg']}
                      limitFileSize={5}
                      changeLoading={changeLoading}
                      uploadType={EUploadFileType.IMAGE}
                      uploadCategory={EMediaCategory.CONTENT}
                    />
                  )
                }}
              />
              <SelectMedia
                isNotGrid
                onClickSelectMedia={() => {
                  setSelectedField('image')
                  setShowModalSelectMedia(true)
                }}
                isLoadingMedia={isLoading}
              />
            </div>

            <div className="flex flex-col gap-1">
              <Controller
                name="logoMediaId"
                control={control}
                render={({ field: { onChange }, fieldState: { error } }) => {
                  onChangeLogoRef.current = onChange
                  return (
                    <CustomDragger
                      label="Logo"
                      name="logoMediaId"
                      id="logoMediaId"
                      containerClassName="mt-3"
                      alignment="col"
                      onLoadEnd={(data) => {
                        setValue('logoMediaId', data?.id ?? 0)
                        setValue(
                          'logoUrl',
                          data?.url || data?.original || data?.baseUrl || ''
                        )
                        onChange(data?.id ?? 0)
                        setLogoSrc(
                          data?.url || data?.original || data?.baseUrl || ''
                        )
                      }}
                      errors={error?.message}
                      initResource={logoSrc}
                      reset={!logoSrc}
                      allowFileTypes={['image/png', 'image/jpeg', 'image/jpg']}
                      limitFileSize={5}
                      changeLoading={changeLoading}
                      uploadType={EUploadFileType.IMAGE}
                      uploadCategory={EMediaCategory.CONTENT}
                    />
                  )
                }}
              />
              <SelectMedia
                isNotGrid
                onClickSelectMedia={() => {
                  setSelectedField('logo')
                  setShowModalSelectMedia(true)
                }}
                isLoadingMedia={isLoading}
              />
            </div>

            <div className="space"></div>
          </InputWrapperGridStyled>

          {/* Display orderIndex and parent title as read-only fields */}
          {item && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Order Index
                  </label>
                  <div className="px-3 py-2 bg-white border border-gray-200 rounded text-sm">
                    {item.orderIndex}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Parent Title
                  </label>
                  <div className="px-3 py-2 bg-white border border-gray-200 rounded text-sm">
                    {item.parent?.title || 'None'}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="mt-6">
            <div className="flex items-center mt-5 justify-end">
              <Button
                type="ghost"
                size="middle"
                className="submit__btn login-btn mr-10"
                onClick={onClose}
                disabled={isLoading}
              >
                {'Cancel'}
              </Button>
              <Button
                htmlType="submit"
                type="primary"
                size="middle"
                className="submit__btn login-btn"
                loading={isLoading}
                disabled={isLoading}
                onClick={handleSubmit(onSave)}
              >
                {'Save'}
              </Button>
            </div>
          </div>
        </form>
      </div>
      {showModalSelectMedia ? (
        <>
          <SelectMediaModal
            isOpen={showModalSelectMedia}
            onClose={onCloseModalSelectMedia}
            mediaType={EMediaType.IMAGE}
            category={EMediaCategory.CONTENT}
            onSelectMedia={onSelectMedia}
          />
        </>
      ) : (
        <></>
      )}
    </AppModal>
  )
}
